import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Scale,
  Plus,
  User,
  LogOut,
  Settings,
  LayoutDashboard,
  Menu,
  X,
  HelpCircle,
  Bell,
  LogIn,
} from 'lucide-react';
import { useState } from 'react';
import { NotificationBell } from '../notifications/NotificationBell';

export const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/discover');
  };

  const handleStartCampaign = () => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent('/campaigns/new')}`);
      return;
    }
    navigate('/campaigns/new');
  };

  const publicLinks = [
    { href: '/discover', label: 'Discover', icon: Scale },
    { href: '/how-to-use', label: 'How to Use', icon: HelpCircle },
  ];
  const memberLinks = [
    { href: '/my-campaigns', label: 'My Campaigns', icon: LayoutDashboard },
  ];
  const navLinks = user ? [publicLinks[0], ...memberLinks, publicLinks[1]] : publicLinks;

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 glass border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/discover" className="flex items-center gap-2 group" data-testid="nav-logo">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center group-hover:scale-105 transition-transform">
              <Scale className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold text-foreground tracking-tight">
              Fund<span className="text-primary">fair</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {isAdmin && (
              <Link
                to="/admin"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive('/admin')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
              >
                Admin
              </Link>
            )}
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <Button
                  onClick={handleStartCampaign}
                  className="hidden sm:inline-flex rounded-full gap-2 btn-bounce"
                  data-testid="create-campaign-btn"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden lg:inline">Start Campaign</span>
                </Button>

                <NotificationBell />

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="rounded-full w-10 h-10 p-0"
                      data-testid="user-menu-btn"
                    >
                      <div className="w-full h-full rounded-full bg-secondary flex items-center justify-center">
                        <User className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium">{user?.username || user?.email}</p>
                      <p className="text-xs text-muted-foreground">
                        {isAdmin ? 'Administrator' : 'Member'}
                      </p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')}>
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/my-campaigns')}>
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      My Campaigns
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/notifications')} data-testid="user-menu-notifications">
                      <Bell className="w-4 h-4 mr-2" />
                      Notifications
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/notifications/preferences')} data-testid="user-menu-notification-prefs">
                      <Settings className="w-4 h-4 mr-2" />
                      Notification Preferences
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')}>
                        <Settings className="w-4 h-4 mr-2" />
                        Admin Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="navbar-logout-btn">
                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`)}
                  className="rounded-full gap-2"
                  data-testid="navbar-login-btn"
                >
                  <LogIn className="w-4 h-4" />
                  Log in
                </Button>
                <Button
                  onClick={() => navigate(`/signup?next=${encodeURIComponent(location.pathname + location.search)}`)}
                  className="rounded-full gap-2 btn-bounce"
                  data-testid="navbar-signup-btn"
                >
                  Sign up
                </Button>
              </>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border/50 animate-fade-in">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive('/admin')
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                  }`}
                >
                  <Settings className="w-5 h-5" />
                  Admin
                </Link>
              )}
              {user ? (
                <button
                  type="button"
                  onClick={() => { setMobileMenuOpen(false); handleStartCampaign(); }}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium bg-primary text-primary-foreground mt-2 w-full text-left"
                >
                  <Plus className="w-5 h-5" />
                  Start Campaign
                </button>
              ) : (
                <div className="flex flex-col gap-2 mt-2">
                  <Link
                    to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium bg-secondary text-foreground"
                  >
                    <LogIn className="w-5 h-5" />
                    Log in
                  </Link>
                  <Link
                    to={`/signup?next=${encodeURIComponent(location.pathname + location.search)}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium bg-primary text-primary-foreground"
                  >
                    Sign up
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
